import { Fragment, lazy, Suspense } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import AuthGuard from 'src/components/AuthGuard';
import GuestGuard from 'src/components/GuestGuard';
import LoadingScreen from 'src/components/LoadingScreen';
import TherapistRouter from 'src/components/TherapistRouter';
import DashboardLayout from 'src/layouts/DashboardLayout';
import MainLayout from 'src/layouts/MainLayout';

type Routes = {
  exact?: boolean;
  path?: string | string[];
  guard?: any;
  layout?: any;
  component?: any;
  routes?: Routes;
  router?: any;
}[];

export const renderRoutes = (routes: Routes = []): JSX.Element => {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Switch>
        {routes.map((route, i) => {
          const Guard = route.guard || Fragment;
          const Layout = route.layout || Fragment;
          const Component = route.component;
          const CustomRouter = route.router || Fragment;

          return (
            <Route
              key={i}
              path={route.path}
              exact={route.exact}
              render={(props) => (
                <Guard>
                  <CustomRouter>
                    <Layout>
                      {route.routes ? renderRoutes(route.routes) : <Component {...props} />}
                    </Layout>
                  </CustomRouter>
                </Guard>
              )}
            />
          );
        })}
      </Switch>
    </Suspense>
  );
};

const routes: Routes = [
  {
    exact: true,
    path: '/404',
    component: lazy(() => import('src/views/errors/NotFoundView')),
  },
  {
    path: '/app',
    guard: AuthGuard,
    layout: DashboardLayout,
    routes: [
      // divergence views
      {
        exact: true,
        path: '/app/dashboard',
        router: TherapistRouter,
        component: lazy(() => import('src/views/therapist/dashboard/Dashboard')),
      },
      {
        exact: true,
        path: '/app/clients',
        router: TherapistRouter,
        component: lazy(() => import('src/views/therapist/clients/ClientListView')),
      },
      {
        exact: true,
        path: '/app/users',
        router: TherapistRouter,
        component: lazy(() => import('src/views/therapist/users/UserListView')),
      },
      {
        exact: true,
        path: '/app/therapist/:therapistId/clients/:clientId/biofeedback/:sessionComponentId',
        router: TherapistRouter,
        component: lazy(() => import('src/views/liveSession/SessionV2')),
      },
      {
        exact: false,
        path: '/app/therapist/:therapistId/clients/:clientId',
        router: TherapistRouter,
        component: lazy(() => import('src/views/therapist/clients/ClientDetailsView')),
      },
      {
        exact: true,
        router: TherapistRouter,
        path: '/app/therapist/:therapistId/client/:clientId/sessions/:sessionId/sessionoverview',
        component: lazy(
          () =>
            import(
              'src/views/therapist/clients/ClientDetailsView/Details/SessionOverview/SessionOverview'
            )
        ),
      },
      {
        exact: true,
        path: '/app/therapist/:therapistId/clients/:clientId/sessions',
        router: TherapistRouter,
        component: lazy(() => import('src/views/therapist/clients/ClientDetailsView')),
      },
      {
        exact: true,
        path: '/app/therapist/:therapistId/clients/:clientId/sessions/:sessionId/selection/:selectionId/recording/:sessionComponentId',
        router: TherapistRouter,
        component: lazy(() => import('src/components/Assessment/Assessment')),
      },
      {
        exact: true,
        path: '/app/therapist/:therapistId/clients/:clientId/questionnaires',
        router: TherapistRouter,
        component: lazy(() => import('src/views/therapist/clients/ClientDetailsView')),
      },
      {
        exact: true,
        path: '/app/devices',
        router: TherapistRouter,
        component: lazy(() => import('src/views/therapist/devices/index')),
      },
      {
        exact: true,
        path: '/app/feedbackresponses',
        router: TherapistRouter,
        component: lazy(() => import('src/views/therapist/feedbackResponse/FeedbackResponse')),
      },
      {
        exact: true,
        path: '/app/protocols',
        router: TherapistRouter,
        component: lazy(() => import('src/views/therapist/protocols/ProtocolListView')),
      },
      {
        exact: true,
        path: '/app/therapist/:therapistId/protocols/:protocolId',
        router: TherapistRouter,
        component: lazy(() => import('src/views/therapist/protocols/ProtocolDetailView')),
      },
      {
        exact: true,
        path: '/app/sessions',
        router: TherapistRouter,
        component: lazy(() => import('src/views/therapist/sessions/SessionListView')),
      },
      {
        exact: true,
        path: '/app/therapist/:therapistId/client/:clientId/sessions/:sessionId',
        router: TherapistRouter,
        component: lazy(() => import('src/views/therapist/sessions/SessionDetailView')),
      },
      {
        exact: true,
        path: '/app/assessments',
        router: TherapistRouter,
        component: lazy(() => import('src/views/therapist/assessments')),
      },
      {
        exact: true,
        path: '/app/eeg-files',
        router: TherapistRouter,
        component: lazy(() => import('src/views/therapist/EEG-Files/EEGListView')),
      },
      {
        exact: true,
        path: '/app/eeg-upload',
        router: TherapistRouter,
        component: lazy(() => import('src/views/therapist/EEG-Upload')),
      },
      {
        exact: true,
        path: '/app/questionnaires',
        router: TherapistRouter,
        component: lazy(() => import('src/views/therapist/questionnaires/QuestionnairesListView')),
      },
      {
        exact: true,
        path: '/app/questionnaire/:questionnaireId',
        router: TherapistRouter,
        component: lazy(
          () => import('src/views/therapist/questionnaires/QuestionnaireDetailsView')
        ),
      },
      {
        exact: true,
        path: '/app/account',
        component: lazy(() => import('src/views/account')),
      },
      {
        exact: true,
        path: '/app',
        component: () => <Redirect to="/app/dashboard" />,
      },
      {
        component: () => <Redirect to="/404" />,
      },
    ],
  },
  {
    path: '*',
    layout: MainLayout,
    routes: [
      {
        exact: true,
        guard: GuestGuard,
        path: '/',
        component: lazy(() => import('src/views/auth/LoginView')),
      },
      {
        exact: true,
        guard: GuestGuard,
        path: '/login',
        component: lazy(() => import('src/views/auth/LoginView')),
      },
      {
        exact: true,
        path: '/register',
        component: lazy(() => import('src/views/auth/RegisterView')),
      },
      {
        exact: true,
        path: '/payment',
        component: lazy(() => import('src/views/auth/Payment')),
      },
      {
        exact: true,
        path: '/pastdue',
        component: lazy(() => import('src/views/auth/SubscriptionPastDue')),
      },
      {
        exact: true,
        path: '/survey',
        component: lazy(() => import('src/views/auth/Survey')),
      },
      {
        exact: true,
        path: '/purchase-device',
        component: lazy(() => import('src/views/auth/PurchaseDevice')),
      },
      {
        exact: true,
        path: '/onboard-client',
        component: lazy(() => import('src/views/auth/RegisterView')),
      },
      {
        exact: true,
        guard: GuestGuard,
        path: '/forgot-password',
        component: lazy(() => import('src/views/auth/ForgotPassword')),
      },
      {
        exact: true,
        path: '/welcome',
        component: lazy(() => import('src/views/auth/Welcome')),
      },
      {
        exact: true,
        path: '/request-verification',
        component: lazy(() => import('src/views/auth/Welcome/Request')),
      },
      {
        exact: true,
        path: '/verification',
        component: lazy(() => import('src/views/auth/Welcome/Verification')),
      },
      {
        exact: true,
        path: '/404',
        component: lazy(() => import('src/views/errors/NotFoundView')),
      },
      {
        exact: true,
        path: '/aw_snap',
        component: lazy(() => import('src/views/errors/AwSnapView')),
      },
      {
        component: () => <Redirect to="/404" />,
      },
    ],
  },
];

export default routes;
